//? Custom Overlay Component
// - overlay-name: name of the overlay to be used in the URL hash
// - id: unique id for the overlay
// - classes: menu-overlay-content--left, menu-overlay-content--right, menu-overlay-content--full
// - menu-overlay-above-content-context: an id of the div that should be the context for above overlay elements that are outside of the overlay
// - add "menu-overlay-above-content" class to elements inside the menu-overlay-above-content-context div to make them visible when overlay is open

/** 
<menu-overlay-component id="myOverlay" overlay-name="overlay-left">
  <div class="menu-overlay-container">
    <div class="menu-overlay-content menu-overlay-content--left">
      <h2 class="menu-overlay-title"></h2>
      <div class="menu-overlay-body">
        <div
          class="menu-overlay-body-content"
          content-name="myInnerContentName"
        ></div>
      </div>
      <button class="menu-overlay-close">Close</button>

      <p>Pop up left</p>
    </div>
  </div>
</menu-overlay-component>

<menu-overlay-component id="myOverlay2" overlay-name="overlay-right">
  <div class="menu-overlay-container">
    <div class="menu-overlay-content menu-overlay-content--right">
      <h2 class="menu-overlay-title"></h2>
      <div class="menu-overlay-body">
        <div
          class="menu-overlay-body-content"
          content-name="myInnerContentName"
        ></div>
      </div>
      <button class="menu-overlay-close">Close</button>

      <p>Pop up right</p>
    </div>
  </div>
</menu-overlay-component>

<button class="menu-overlay-trigger" data-target="myOverlay" data-content-target="myInnerContentName">Open Popup 1</button>
<button class="menu-overlay-trigger" data-target="myOverlay2" data-content-target="myInnerContentName">Open Popup 2</button>
<button class="menu-overlay-trigger" data-target="myOverlay3" data-content-target="myInnerContentName">Open Popup 3</button>
 */

class MenuOverlayComponent extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.menu-overlay-container');
    this.content = this.querySelector('.menu-overlay-content');
    this.title = this.querySelector('.menu-overlay-title');
    this.body = this.querySelector('.menu-overlay-body');
    this.closeButton = this.querySelector('.menu-overlay-close');
    this.triggerButtons = document.querySelectorAll(`.menu-overlay-trigger[data-target="${this.id}"]`);
    this.bodyContents = this.querySelectorAll('.menu-overlay-body-content');
    this.overlayName = this.getAttribute('overlay-name') || 'overlay';
    this.activeTriggerButton = null;
    this.aboveContentSiblings = document
      .getElementById(this.getAttribute('menu-overlay-above-content-context'))
      ?.querySelectorAll('.menu-overlay-above-content');
    this.aboveContentContext = document.getElementById(this.getAttribute('menu-overlay-above-content-context'));

    // Fix when scroll if not past header yet
    this.announcementBarElement = document.querySelector('.announcement-bar-section');
    this.headerElement = document.querySelector('.shopify-section.section-header');
    this.headerIsStickyVersion = this.headerElement.classList.contains('shopify-section-group-header-group-sticky');
    this.headerElementPreviousClasslist = this.headerElement.classList;
    this.headerClassesToAddWhenOpen = ['animate'];

    if (!this.overlay || !this.content) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.overlay.addEventListener('click', this.onOverlayClick.bind(this));
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.closeOverlay.bind(this));
    }
    this.triggerButtons.forEach((button) => {
      button.addEventListener('click', this.openOverlay.bind(this, button));
    });
    window.addEventListener('keyup', this.onKeyUp.bind(this));
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.closeOverlay();
    }
  }

  onKeyUp(event) {
    if (event.key === 'Escape') {
      this.closeOverlay();
    }
  }

  openOverlay(button) {
    this.classList.add('is-open');
    // remove active class from menu option
    if (this.activeTriggerButton) {
      this.activeTriggerButton.classList.remove('is-active');
    }
    // set active class to the new menu option
    this.activeTriggerButton = button;
    this.activeTriggerButton?.classList.add('is-active');
    const targetBodyContent = button.getAttribute('data-content-target');
    this.overlay.classList.add('is-open');
    // go throught possible body contents
    this.bodyContents.forEach((bodyContent) => {
      // if it's the target add open class
      if (bodyContent.getAttribute('content-name') == targetBodyContent) {
        bodyContent.classList.add('is-open');
        bodyContent.querySelectorAll('a').forEach((link, index) => {
          link.setAttribute('tabindex', 0);
        });
      } else {
        // if it's not the target hide it
        bodyContent.classList.remove('is-open');
        bodyContent.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', -1);
        });
      }
    });

    // set above-content siblings z-index
    if (this.aboveContentSiblings?.length) {
      this.aboveContentSiblings.forEach((sibling) => {
        sibling.classList.add('is-above-content');
        // keep, if they complain about the icon not being over the overlay
        // if(sibling.classList.contains("header__search")) {
        //   sibling.classList.add("is-above-above-content")
        // } else {
        //   sibling.classList.add("is-above-content")
        // }
      });
    }
    this.overlay.setAttribute('tabIndex', 0);

    if (!this.headerElement.classList.contains('scrolled-past-header')) {
      this.headerElement.classList.add(...this.headerClassesToAddWhenOpen);
      // fix for announcement bar
      if (this.announcementBarElement && window.scrollY < this.announcementBarElement.offsetHeight) {
        this.headerElement.style.top = `${window.scrollY}px`;
        this.announcementBarElement.style.marginBottom = `${window.scrollY - this.announcementBarElement.offsetHeight}px`;
        this.announcementBarElement.style.transform = 'translate3d(0,-100%,0)';
      } else {
        this.headerElement.style.top = `${window.scrollY}px`;
      }
    }

    trapFocus(this.aboveContentContext);
    document.body.style.overflow = 'hidden';
    this.dispatchEvent(new CustomEvent('overlayOpened'));
  }

  closeOverlay(button) {
    this.classList.remove('is-open');
    this.activeTriggerButton?.classList.remove('is-active');
    this.overlay.setAttribute('tabIndex', -1);
    this.overlay.classList.remove('is-open');

    this.bodyContents.forEach((bodyContent) => {
      // if it's not the target hide it
      if (bodyContent.classList.contains('is-open')) {
        bodyContent.classList.remove('is-open');
        bodyContent.querySelectorAll('a').forEach((link) => {
          link.setAttribute('tabindex', -1);
        });
      }
    });

    if (!this.headerElement.classList.contains('scrolled-past-header')) {
      this.headerElement.style.removeProperty('top');
      if (this.announcementBarElement && window.scrollY < this.announcementBarElement.offsetHeight) {
        this.announcementBarElement.style.removeProperty('transform');
        this.announcementBarElement.style.removeProperty('margin-bottom');
      }
    }

    // set above-content siblings z-index after animation
    if (this.aboveContentSiblings?.length) {
      setTimeout(() => {
        this.aboveContentSiblings.forEach((sibling) => {
          sibling.classList.remove('is-above-content');
        });

        this.bodyContents.forEach((bodyContent) => {
          // fix announcement bar
          if (!this.headerElement.classList.contains('scrolled-past-header')) {
            if (this.announcementBarElement && window.scrollY < this.announcementBarElement.offsetHeight) {
              bodyContent.style.removeProperty('top');
            }
          }
        });

        removeTrapFocus(this.aboveContentContext);
        document.body.style.overflow = 'auto';
      }, 500);
    }

    this.dispatchEvent(new CustomEvent('overlayClosed'));
  }
}

customElements.define('menu-overlay-component', MenuOverlayComponent);
